import { SubscriptionBenefitList } from '@/components/subscription-conditions'
import { useSubscriptionConditions } from '@/hooks/use-subscription-conditions'
import { CustomerCourseAccess, CreditTypeSelect } from '@/components/admin/credit-settings'
import { TypedCreditList } from '@/components/typed-credit-list'
import { useTypedCreditBalances } from '@/hooks/use-typed-credit-balances'
import { getCreditWeekStartKey } from '@/lib/date'
import { type CreditType, REGULAR_CREDIT_TYPE } from '@/lib/credits-api'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { DatesSetArg, EventClickArg, EventContentArg, EventInput } from '@fullcalendar/core'
import deLocale from '@fullcalendar/core/locales/de'
import dayGridPlugin from '@fullcalendar/daygrid'
import interactionPlugin from '@fullcalendar/interaction'
import FullCalendar from '@fullcalendar/react'
import timeGridPlugin from '@fullcalendar/timegrid'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { format } from 'date-fns'
import { de } from 'date-fns/locale'
import { Navigate, useNavigate, useParams } from 'react-router-dom'
import { toast } from 'sonner'

import {
  AlertCircleIcon,
  CoinsIcon,
  Loader2Icon,
  Megaphone,
  PlusIcon,
  Trash2Icon,
} from 'lucide-react'
import { CancelBookingConfirmDialog } from '@/components/cancel-booking-confirm-dialog'
import { SessionDetailDialog, type SessionDetailData } from '@/components/session-detail-dialog'
import { MobileCalendarToolbar } from '@/components/mobile-calendar-toolbar'
import { PaymentEntriesTable } from '@/components/shared/payment-entries-table'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  adjustManagedUserCredits,
  bookManagedUserSession,
  cancelManagedUserBooking,
  getConfirmedBookingCountsBySession,
  getCurrentUserRole,
  getDiscounts,
  getMemberDiscountAssignments,
  getManagedUserBookings,
  getManagedUserCreditAdjustments,
  getManagedUserPackages,
  getManagedUserPaymentEntries,
  getManagedUserProfile,
  getManagedUserSubscriptionStatus,
  getManagedUserSessionBookingQuote,
  getSessions,
  manageMemberDiscount,
  updateManagedUserSubscriptionPrice,
} from '@/lib/api'
import {
  MOBILE_CALENDAR_BREAKPOINT,
  getCalendarToolbarTitle,
  getInitialCalendarWindowWidth,
  getResponsiveCalendarLayout,
} from '@/lib/calendar-layout'
import { DEFAULT_TABLE_PAGE_SIZE, TABLE_PAGE_SIZE_OPTIONS } from '@/lib/constants'
import { formatDateTime } from '@/lib/date'
import { getErrorMessage, translateErrorCode } from '@/lib/error-messages'
import { queryKeys } from '@/lib/query-keys'
import {
  formatCreditAmount,
  formatCourseCredits,
  formatCredits,
  isRecurringSubscription,
  parseCreditAmount,
} from '@/lib/subscriptions'
import { cn } from '@/lib/utils'
import type {
  ManagedUserBooking,
  ManagedUserCreditAdjustment,
  MemberDiscountAssignment,
} from '@/types/domain'

import '@/pages/member/member-calendar-page.css'

const START_TIME = '06:00:00'
const END_TIME = '22:00:00'
const SLOT_DURATION = '00:15:00'
const DEFAULT_SCROLL_TIME = '08:00:00'
const DEFAULT_COURSE_COLOR = '#38BDF8'

// Reasons from get_managed_user_session_booking_quote other than missing credits.
const STAFF_BOOKING_BLOCKS: Record<string, { title: string; description: string }> = {
  SESSION_CANCELLED: { title: 'Termin abgesagt', description: 'Dieser Termin wurde abgesagt.' },
  SESSION_NOT_BOOKABLE: { title: 'Kurs nicht aktiv', description: 'Der Kurs dieses Termins ist deaktiviert.' },
  COURSE_APPROVAL_REQUIRED: {
    title: 'Freischaltung fehlt',
    description: 'Das Mitglied ist für diesen Kurs noch nicht freigeschaltet.',
  },
  COURSE_TERMS_REQUIRED: {
    title: 'Kurs-AGB nicht akzeptiert',
    description: 'Das Mitglied muss zuerst die aktuellen Kurs-AGB akzeptieren.',
  },
  ALREADY_BOOKED: { title: 'Bereits eingebucht', description: 'Das Mitglied ist für diesen Termin bereits eingebucht.' },
  SESSION_FULL: { title: 'Termin ausgebucht', description: 'Alle Plätze dieses Termins sind vergeben.' },
}

type CreditAction = 'add' | 'remove'
type UserDetailTab = 'profile' | 'bookings' | 'packages' | 'credits' | 'discounts' | 'access' | 'enrollment'
type PackageDetailsTab = 'purchased-packages' | 'payments'
type CalendarView = 'timeGridDay' | 'timeGridWeek' | 'dayGridMonth'

type CalendarSession = {
  id: string
  courseName: string
  courseDescription: string | null
  startsAt: string
  endsAt: string
  registrationUntil: string | null
  cancellationUntil: string | null
  maxParticipants: number
  minParticipants: number | null
  confirmedCount: number
  availableSpots: number
  trainerName: string | null
  trainerId: string | null
  resourceName: string | null
  creditsPerSession: number
}

function roleLabel(role: string): string {
  switch (role) {
    case 'admin':
      return 'Admin'
    case 'trainer':
      return 'Trainer'
    case 'member':
      return 'Teilnehmer'
    default:
      return role
  }
}

function formatDate(value: string | null | undefined): string {
  if (!value) {
    return '-'
  }

  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) {
    return '-'
  }

  return format(parsed, 'dd.MM.yyyy', { locale: de })
}

function formatMoney(cents: number): string {
  return `${(cents / 100).toFixed(2).replace('.', ',')} €`
}

function formatDiscountValue(discount: {
  discount_type: 'percent' | 'amount'
  percent_off: number | null
  amount_cents: number | null
}): string {
  if (discount.discount_type === 'percent') {
    return `${Number(discount.percent_off ?? 0).toString().replace('.', ',')} %`
  }

  return formatMoney(discount.amount_cents ?? 0)
}

function bookingStatusLabel(status: string, endsAt: string): {
  label: string
  variant: 'default' | 'secondary' | 'destructive'
} {
  if (status === 'cancelled') {
    return { label: 'Storniert', variant: 'destructive' }
  }

  if (new Date(endsAt) < new Date()) {
    return { label: 'Besucht', variant: 'secondary' }
  }

  return { label: 'Gebucht', variant: 'default' }
}

function packageStatusLabel(pkg: {
  billing_mode: 'payment' | 'subscription'
  remaining_credits: number
  is_active: boolean
}): {
  label: string
  variant: 'default' | 'secondary' | 'destructive'
} {
  if (pkg.billing_mode === 'payment') {
    if (pkg.remaining_credits > 0) {
      return { label: 'Guthaben verfügbar', variant: 'secondary' }
    }

    return { label: 'Aufgebraucht', variant: 'destructive' }
  }

  return pkg.is_active
    ? { label: 'Aktiv', variant: 'default' }
    : { label: 'Inaktiv', variant: 'secondary' }
}

export function UserDetailPage() {
  const { userId } = useParams<{ userId: string }>()
  const queryClient = useQueryClient()
  const calendarRef = useRef<FullCalendar | null>(null)
  const navigate = useNavigate()
  const [windowWidth, setWindowWidth] = useState(getInitialCalendarWindowWidth)
  const { calendarView, visibleDays } = useMemo(
    () => getResponsiveCalendarLayout(windowWidth),
    [windowWidth],
  )

  const [creditAmount, setCreditAmount] = useState('1')
  const [creditTypeId, setCreditTypeId] = useState(REGULAR_CREDIT_TYPE)
  const [creditPaymentAmount, setCreditPaymentAmount] = useState('')
  const [creditReason, setCreditReason] = useState('')
  const [creditAction, setCreditAction] = useState<CreditAction | null>(null)
  const [selectedDiscountId, setSelectedDiscountId] = useState('')
  const [discountToRemove, setDiscountToRemove] = useState<MemberDiscountAssignment | null>(null)
  const [removeDiscountConfirmOpen, setRemoveDiscountConfirmOpen] = useState(false)
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null)
  const [confirmCancelOpen, setConfirmCancelOpen] = useState(false)
  const [activeTab, setActiveTab] = useState<UserDetailTab>('profile')
  const [packagesContentTab, setPackagesContentTab] =
    useState<PackageDetailsTab>('purchased-packages')
  const [bookingsCourseFilter, setBookingsCourseFilter] = useState('all')
  const [bookingsTrainerFilter, setBookingsTrainerFilter] = useState('all')
  const [bookingsPage, setBookingsPage] = useState(1)
  const [bookingsPageSize, setBookingsPageSize] = useState(DEFAULT_TABLE_PAGE_SIZE)
  const [packagesPage, setPackagesPage] = useState(1)
  const [packagesPageSize, setPackagesPageSize] = useState(DEFAULT_TABLE_PAGE_SIZE)
  const [billingEntriesPage, setBillingEntriesPage] = useState(1)
  const [billingEntriesPageSize, setBillingEntriesPageSize] = useState(DEFAULT_TABLE_PAGE_SIZE)
  const [creditAdjustmentsPage, setCreditAdjustmentsPage] = useState(1)
  const [creditAdjustmentsPageSize, setCreditAdjustmentsPageSize] =
    useState(DEFAULT_TABLE_PAGE_SIZE)
  const [currentCalendarView, setCurrentCalendarView] = useState<CalendarView>(calendarView)
  const [calendarTitle, setCalendarTitle] = useState('')
  const [visibleRange, setVisibleRange] = useState(() => ({
    from: new Date(Date.now() - 1000 * 60 * 60 * 24 * 7).toISOString(),
    to: new Date(Date.now() + 1000 * 60 * 60 * 24 * 30).toISOString(),
  }))

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth)
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const currentUserQuery = useQuery({
    queryKey: queryKeys.currentUserProfile,
    queryFn: getCurrentUserRole,
    retry: false,
    refetchOnWindowFocus: false,
  })

  const isAdmin = currentUserQuery.data?.role === 'admin'
  const canManageUsers =
    isAdmin || currentUserQuery.data?.role === 'trainer'

  const profileQuery = useQuery({
    queryKey: queryKeys.managedUserProfile(userId ?? ''),
    queryFn: () => getManagedUserProfile(userId!),
    enabled: Boolean(userId && canManageUsers),
  })

  const targetIsMember = profileQuery.data?.role === 'member'
  const shouldLoadBookings = activeTab === 'bookings' || activeTab === 'enrollment'
  const shouldLoadPackages = activeTab === 'packages' && targetIsMember
  const shouldLoadPaymentEntries = activeTab === 'packages' && targetIsMember && isAdmin
  const shouldLoadSubscription =
    (activeTab === 'credits' || activeTab === 'enrollment') && targetIsMember
  const shouldLoadCreditAdjustments =
    (activeTab === 'credits' || activeTab === 'packages') && targetIsMember
  const shouldLoadDiscounts = activeTab === 'discounts' && targetIsMember
  const shouldLoadEnrollmentData = activeTab === 'enrollment' && targetIsMember

  const bookingsQuery = useQuery({
    queryKey: queryKeys.managedUserBookings(userId ?? ''),
    queryFn: () => getManagedUserBookings(userId!),
    enabled: Boolean(userId && canManageUsers && shouldLoadBookings),
  })

  const packagesQuery = useQuery({
    queryKey: queryKeys.managedUserPackages(userId ?? ''),
    queryFn: () => getManagedUserPackages(userId!),
    enabled: Boolean(userId && canManageUsers && shouldLoadPackages),
  })

  const paymentEntriesQuery = useQuery({
    queryKey: queryKeys.managedUserPaymentEntries(userId ?? ''),
    queryFn: () => getManagedUserPaymentEntries(userId!),
    enabled: Boolean(userId && isAdmin && shouldLoadPaymentEntries),
  })

  const subscriptionQuery = useQuery({
    queryKey: queryKeys.managedUserSubscription(userId ?? ''),
    queryFn: () => getManagedUserSubscriptionStatus(userId!),
    enabled: Boolean(userId && canManageUsers && shouldLoadSubscription),
  })

  const typedCreditBalancesQuery = useTypedCreditBalances({
    weekStarts: [getCreditWeekStartKey(new Date())],
    userId,
    enabled: Boolean(userId && canManageUsers && shouldLoadSubscription),
  })

  const subscriptionConditionsQuery = useSubscriptionConditions(
    userId,
    Boolean(userId && canManageUsers && shouldLoadSubscription),
  )

  const creditAdjustmentsQuery = useQuery({
    queryKey: queryKeys.managedUserCreditAdjustments(userId ?? ''),
    queryFn: () => getManagedUserCreditAdjustments(userId!),
    enabled: Boolean(userId && canManageUsers && shouldLoadCreditAdjustments),
  })

  const discountsQuery = useQuery({
    queryKey: queryKeys.discounts,
    queryFn: getDiscounts,
    enabled: Boolean(userId && isAdmin && shouldLoadDiscounts),
  })

  const discountAssignmentsQuery = useQuery({
    queryKey: queryKeys.memberDiscountAssignments(userId ?? ''),
    queryFn: () => getMemberDiscountAssignments(userId!),
    enabled: Boolean(userId && isAdmin && shouldLoadDiscounts),
  })

  const sessionsQuery = useQuery({
    queryKey: queryKeys.sessions(visibleRange.from, visibleRange.to),
    queryFn: () => getSessions(visibleRange),
    enabled: Boolean(userId && canManageUsers && shouldLoadEnrollmentData),
  })

  const sessionCountsQuery = useQuery({
    queryKey: queryKeys.sessionBookingCounts,
    queryFn: getConfirmedBookingCountsBySession,
    enabled: Boolean(userId && canManageUsers && shouldLoadEnrollmentData),
  })

  const creditMutation = useMutation({
    mutationFn: ({
      delta,
      reason,
      amountPaidCents,
      operationKey,
    }: {
      delta: number
      reason?: string
      amountPaidCents?: number | null
      operationKey?: string
    }) =>
      adjustManagedUserCredits({
        userId: userId!,
        creditTypeId,
        delta,
        reason,
        amountPaidCents,
        operationKey,
      }),
    onSuccess: async ({ total_credits }) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.userCreditBalancesBase })
      const creditTypeName = queryClient
        .getQueryData<CreditType[]>(['credit-types'])
        ?.find((type) => type.id === creditTypeId)?.name
      await queryClient.invalidateQueries({ queryKey: queryKeys.managedUserPackages(userId ?? '') })
      await queryClient.invalidateQueries({
        queryKey: queryKeys.managedUserSubscription(userId ?? ''),
      })
      await queryClient.invalidateQueries({
        queryKey: queryKeys.managedUserPaymentEntries(userId ?? ''),
      })
      await queryClient.invalidateQueries({
        queryKey: queryKeys.managedUserCreditAdjustments(userId ?? ''),
      })
      setCreditReason('')
      setCreditPaymentAmount('')
      toast.success(`Guthaben aktualisiert. Aktuell ${creditTypeName ?? 'Credits'}: ${total_credits}.`)
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
    onSettled: () => {
      setCreditAction(null)
    },
  })

  const bookingMutation = useMutation({
    mutationFn: (sessionId: string) =>
      bookManagedUserSession({
        userId: userId!,
        sessionId,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.managedUserBookings(userId ?? '') })
      await queryClient.invalidateQueries({
        queryKey: queryKeys.managedUserSubscription(userId ?? ''),
      })
      await queryClient.invalidateQueries({ queryKey: queryKeys.sessionBookingCounts })
      await queryClient.invalidateQueries({ queryKey: queryKeys.sessionsBase })
      setSelectedSessionId(null)
      toast.success('Teilnehmer erfolgreich eingebucht.')
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })

  const discountMutation = useMutation({
    mutationFn: ({
      discountId,
      action,
    }: {
      discountId: string
      action: 'assign' | 'remove'
    }) =>
      manageMemberDiscount({
        userId: userId!,
        discountId,
        action,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: queryKeys.memberDiscountAssignments(userId ?? ''),
      })
      await queryClient.invalidateQueries({ queryKey: queryKeys.discounts })
      await queryClient.invalidateQueries({
        queryKey: queryKeys.managedUserPaymentEntries(userId ?? ''),
      })
      setSelectedDiscountId('')
      setDiscountToRemove(null)
      setRemoveDiscountConfirmOpen(false)
      toast.success('Rabatte wurden aktualisiert.')
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })

  const subscriptionPriceMutation = useMutation({
    mutationFn: () => updateManagedUserSubscriptionPrice(userId!),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: queryKeys.managedUserSubscription(userId ?? ''),
      })
      await queryClient.invalidateQueries({
        queryKey: queryKeys.adminSubscriptionPackages,
      })
      await queryClient.invalidateQueries({
        queryKey: ['subscription-conditions', userId],
      })
      toast.success('Die aktuellen Abo-Konditionen werden ab der nächsten Abrechnung hinterlegt.')
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })

  const cancelBookingMutation = useMutation({
    mutationFn: (bookingId: string) =>
      cancelManagedUserBooking({
        userId: userId!,
        bookingId,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.managedUserBookings(userId ?? '') })
      await queryClient.invalidateQueries({
        queryKey: queryKeys.managedUserSubscription(userId ?? ''),
      })
      await queryClient.invalidateQueries({ queryKey: queryKeys.sessionBookingCounts })
      await queryClient.invalidateQueries({ queryKey: queryKeys.sessionsBase })
      setSelectedSessionId(null)
      toast.success('Buchung erfolgreich storniert.')
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })

  const bookings = useMemo(() => bookingsQuery.data ?? [], [bookingsQuery.data])
  const activeBookingBySessionId = useMemo(() => {
    const bySessionId = new Map<string, ManagedUserBooking>()
    for (const booking of bookings) {
      if (booking.status !== 'confirmed') {
        continue
      }
      if (!bySessionId.has(booking.session_id)) {
        bySessionId.set(booking.session_id, booking)
      }
    }
    return bySessionId
  }, [bookings])

  const bookedSessionIds = useMemo(() => {
    return new Set(activeBookingBySessionId.keys())
  }, [activeBookingBySessionId])
  const activeBookingCountBySessionId = useMemo(() => {
    const countBySessionId: Record<string, number> = {}
    for (const booking of bookings) {
      if (booking.status !== 'confirmed') {
        continue
      }
      countBySessionId[booking.session_id] = (countBySessionId[booking.session_id] ?? 0) + 1
    }
    return countBySessionId
  }, [bookings])

  const packages = useMemo(() => packagesQuery.data ?? [], [packagesQuery.data])
  const totalPackages = packages.length
  const packagesTotalPages = Math.max(1, Math.ceil(totalPackages / packagesPageSize))
  const packagesCurrentPage = Math.min(packagesPage, packagesTotalPages)
  const paginatedPackages = useMemo(() => {
    const from = (packagesCurrentPage - 1) * packagesPageSize
    return packages.slice(from, from + packagesPageSize)
  }, [packages, packagesCurrentPage, packagesPageSize])
  const paymentEntries = useMemo(() => paymentEntriesQuery.data ?? [], [paymentEntriesQuery.data])
  const totalBillingEntries = paymentEntries.length
  const billingEntriesTotalPages = Math.max(
    1,
    Math.ceil(totalBillingEntries / billingEntriesPageSize),
  )
  const billingEntriesCurrentPage = Math.min(billingEntriesPage, billingEntriesTotalPages)
  const paginatedBillingEntries = useMemo(() => {
    const from = (billingEntriesCurrentPage - 1) * billingEntriesPageSize
    return paymentEntries.slice(from, from + billingEntriesPageSize)
  }, [paymentEntries, billingEntriesCurrentPage, billingEntriesPageSize])
  const bookingCourseOptions = useMemo(() => {
    return Array.from(new Set(bookings.map((booking) => booking.course_name))).sort((a, b) =>
      a.localeCompare(b, 'de'),
    )
  }, [bookings])
  const bookingTrainerOptions = useMemo(() => {
    return Array.from(
      new Set(
        bookings
          .map((booking) => booking.trainer_name)
          .filter((trainerName): trainerName is string => Boolean(trainerName)),
      ),
    ).sort((a, b) => a.localeCompare(b, 'de'))
  }, [bookings])

  // A filter whose course or trainer no longer occurs in the bookings falls back to "all".
  const courseFilter = bookingCourseOptions.includes(bookingsCourseFilter) ? bookingsCourseFilter : 'all'
  const trainerFilter = bookingTrainerOptions.includes(bookingsTrainerFilter) ? bookingsTrainerFilter : 'all'

  const filteredBookings = useMemo(() => {
    return bookings.filter((booking) => {
      const matchesCourse = courseFilter === 'all' || booking.course_name === courseFilter
      const matchesTrainer = trainerFilter === 'all' || booking.trainer_name === trainerFilter
      return matchesCourse && matchesTrainer
    })
  }, [bookings, courseFilter, trainerFilter])

  const totalFilteredBookings = filteredBookings.length
  const bookingsTotalPages = Math.max(1, Math.ceil(totalFilteredBookings / bookingsPageSize))
  const bookingsCurrentPage = Math.min(bookingsPage, bookingsTotalPages)

  const paginatedBookings = useMemo(() => {
    const from = (bookingsCurrentPage - 1) * bookingsPageSize
    return filteredBookings.slice(from, from + bookingsPageSize)
  }, [bookingsCurrentPage, bookingsPageSize, filteredBookings])

  const creditAdjustments = useMemo(
    () => (creditAdjustmentsQuery.data ?? []) as ManagedUserCreditAdjustment[],
    [creditAdjustmentsQuery.data],
  )
  const assignedDiscounts = useMemo(
    () => (discountAssignmentsQuery.data ?? []) as MemberDiscountAssignment[],
    [discountAssignmentsQuery.data],
  )
  const assignedDiscountIds = useMemo(
    () => new Set(assignedDiscounts.map((assignment) => assignment.discount_id)),
    [assignedDiscounts],
  )
  const availableDiscounts = useMemo(
    () =>
      (discountsQuery.data ?? []).filter(
        (discount) => discount.is_active && !assignedDiscountIds.has(discount.id),
      ),
    [assignedDiscountIds, discountsQuery.data],
  )
  const manualReasonByPackageId = useMemo(() => {
    const reasonByPackageId = new Map<string, string>()
    for (const adjustment of creditAdjustments) {
      if (adjustment.delta <= 0 || !adjustment.bucket_subscription_id) {
        continue
      }
      if (!reasonByPackageId.has(adjustment.bucket_subscription_id)) {
        reasonByPackageId.set(adjustment.bucket_subscription_id, adjustment.reason)
      }
    }
    return reasonByPackageId
  }, [creditAdjustments])

  const totalCreditAdjustments = creditAdjustments.length
  const creditAdjustmentsTotalPages = Math.max(
    1,
    Math.ceil(totalCreditAdjustments / creditAdjustmentsPageSize),
  )
  const creditAdjustmentsCurrentPage = Math.min(creditAdjustmentsPage, creditAdjustmentsTotalPages)

  const paginatedCreditAdjustments = useMemo(() => {
    const from = (creditAdjustmentsCurrentPage - 1) * creditAdjustmentsPageSize
    return creditAdjustments.slice(from, from + creditAdjustmentsPageSize)
  }, [creditAdjustments, creditAdjustmentsCurrentPage, creditAdjustmentsPageSize])

  const bookableSessions = useMemo<CalendarSession[]>(() => {
    const sessionRows = sessionsQuery.data ?? []
    const counts = sessionCountsQuery.data ?? {}

    return sessionRows
      .filter((session) => !session.is_cancelled)
      .map((session) => {
        const confirmedCount = Math.max(
          counts[session.id] ?? 0,
          activeBookingCountBySessionId[session.id] ?? 0,
        )
        const availableSpots = Math.max(session.max_participants - confirmedCount, 0)

        return {
          id: session.id,
          courseName: session.courses?.name ?? 'Kurs',
          courseDescription: session.courses?.description ?? null,
          startsAt: session.starts_at,
          endsAt: session.ends_at,
          registrationUntil: session.registration_until,
          cancellationUntil: session.cancellation_until,
          maxParticipants: session.max_participants,
          minParticipants: session.min_participants,
          confirmedCount,
          availableSpots,
          trainerName: session.trainer?.full_name ?? null,
          trainerId: session.trainer?.id ?? null,
          resourceName: session.resource?.name ?? null,
          creditsPerSession: session.courses?.credits_per_session ?? 1,
        }
      })
  }, [activeBookingCountBySessionId, sessionCountsQuery.data, sessionsQuery.data])

  const selectedSession = useMemo(
    () => bookableSessions.find((session) => session.id === selectedSessionId) ?? null,
    [bookableSessions, selectedSessionId],
  )
  const selectedSessionBooking = useMemo(
    () => (selectedSession ? activeBookingBySessionId.get(selectedSession.id) ?? null : null),
    [activeBookingBySessionId, selectedSession],
  )

  const bookingQuoteQuery = useQuery({
    queryKey: queryKeys.managedUserSessionBookingQuote(
      userId ?? 'none',
      selectedSession?.id ?? 'none',
    ),
    queryFn: () =>
      getManagedUserSessionBookingQuote({
        userId: userId!,
        sessionId: selectedSession!.id,
      }),
    enabled: Boolean(
      userId && selectedSession && !selectedSessionBooking && canManageUsers && targetIsMember,
    ),
    retry: false,
  })

  const selectedSessionDetail = useMemo<SessionDetailData | null>(() => {
    if (!selectedSession) {
      return null
    }

    return {
      sessionId: selectedSession.id,
      bookingId: selectedSessionBooking?.booking_id,
      courseName: selectedSession.courseName,
      courseDescription: selectedSession.courseDescription,
      startsAt: selectedSession.startsAt,
      endsAt: selectedSession.endsAt,
      availableSpots: selectedSession.availableSpots,
      maxParticipants: selectedSession.maxParticipants,
      registrationUntil: selectedSession.registrationUntil,
      cancellationUntil:
        selectedSessionBooking?.cancellation_until ?? selectedSession.cancellationUntil,
      status: selectedSessionBooking?.status ?? null,
      minParticipants: selectedSession.minParticipants,
      confirmedCount: selectedSession.confirmedCount,
      resourceName: selectedSession.resourceName,
      creditsRequired:
        selectedSessionBooking?.credits_required ??
        bookingQuoteQuery.data?.credits_required ??
        selectedSession.creditsPerSession,
      subscriptionCreditsUsed:
        selectedSessionBooking?.subscription_credits_used ??
        bookingQuoteQuery.data?.subscription_credits_used ??
        null,
      balanceCreditsUsed:
        selectedSessionBooking?.balance_credits_used ??
        bookingQuoteQuery.data?.balance_credits_used ??
        null,
    }
  }, [bookingQuoteQuery.data, selectedSession, selectedSessionBooking])

  const calendarEvents = useMemo<EventInput[]>(() => {
    return bookableSessions.map((session) => {
      const isBooked = bookedSessionIds.has(session.id)
      const isPast = new Date(session.startsAt) <= new Date()
      const isFull = session.availableSpots <= 0

      return {
        id: session.id,
        title: session.courseName,
        start: session.startsAt,
        end: session.endsAt,
        backgroundColor: isBooked
          ? '#22c55e'
          : isPast
            ? '#6b7280'
            : isFull
              ? '#ef4444'
              : DEFAULT_COURSE_COLOR,
        borderColor: isBooked
          ? '#16a34a'
          : isPast
            ? '#4b5563'
            : isFull
              ? '#dc2626'
              : DEFAULT_COURSE_COLOR,
        textColor: '#ffffff',
        extendedProps: {
          isBooked,
          isFull,
          isPast,
          availableSpots: session.availableSpots,
          minParticipants: session.minParticipants,
          confirmedCount: session.confirmedCount,
        },
      }
    })
  }, [bookableSessions, bookedSessionIds])

  const renderEventContent = useCallback((arg: EventContentArg) => {
    const isBooked = Boolean(arg.event.extendedProps.isBooked)
    const isPast = Boolean(arg.event.extendedProps.isPast)
    const availableSpots = Number(arg.event.extendedProps.availableSpots ?? 0)
    const minParticipants = arg.event.extendedProps.minParticipants
    const confirmedCount = arg.event.extendedProps.confirmedCount
    const isGuaranteed = minParticipants != null && confirmedCount >= minParticipants

    return (
      <div className="p-0.5 text-[10px] leading-tight">
        <div className="flex items-center gap-1">
          <div className="font-bold truncate">{arg.event.title}</div>
          {isGuaranteed && (
            <span className="font-bold text-emerald-500" title="Stattfinden garantiert">
              ✓
            </span>
          )}
        </div>
        <div className="mt-0.5 flex items-center gap-1">
          {isBooked ? (
            <span className="font-semibold text-emerald-600">✓ Gebucht</span>
          ) : isPast ? (
            <span>{availableSpots} frei</span>
          ) : (
            <span>{availableSpots} frei</span>
          )}
        </div>
      </div>
    )
  }, [])

  const handleEventClick = useCallback(
    (arg: EventClickArg) => {
      const nextSession = bookableSessions.find((session) => session.id === arg.event.id)
      if (!nextSession) {
        return
      }

      if (bookedSessionIds.has(nextSession.id)) {
        setSelectedSessionId(nextSession.id)
        return
      }

      if (nextSession.availableSpots <= 0) {
        toast.error('Dieser Termin ist bereits ausgebucht.')
        return
      }

      setSelectedSessionId(nextSession.id)
    },
    [bookableSessions, bookedSessionIds],
  )

  const handleDatesSet = useCallback((arg: DatesSetArg) => {
    const from = arg.start.toISOString()
    const to = arg.end.toISOString()
    setCurrentCalendarView(arg.view.type as CalendarView)
    setCalendarTitle(getCalendarToolbarTitle(arg))

    setVisibleRange((previous) => {
      if (previous.from === from && previous.to === to) {
        return previous
      }

      return { from, to }
    })
  }, [])

  const handleTabChange = useCallback((value: string) => {
    setActiveTab(value as UserDetailTab)
  }, [])

  const isSelectedSessionBooked = selectedSessionBooking != null
  const showMobileToolbar = windowWidth < MOBILE_CALENDAR_BREAKPOINT
  const effectiveCalendarView: CalendarView = currentCalendarView === 'dayGridMonth' ? 'dayGridMonth' : calendarView
  const calendarKey = currentCalendarView === 'dayGridMonth' ? 'dayGridMonth' : `${calendarView}-${visibleDays}`
  const calendarDuration = currentCalendarView === 'dayGridMonth' || visibleDays === 7 ? undefined : { days: visibleDays }
  const calendarViews = [
    { label: 'Tag', value: 'timeGridDay' },
    { label: 'Woche', value: 'timeGridWeek' },
    { label: 'Monat', value: 'dayGridMonth' },
  ]

  const navigateCalendar = useCallback((direction: 'prev' | 'next') => {
    const calendarApi = calendarRef.current?.getApi()
    if (!calendarApi) return
    if (direction === 'next') {
      calendarApi.next()
      return
    }
    calendarApi.prev()
  }, [])

  const goToToday = useCallback(() => {
    calendarRef.current?.getApi().today()
  }, [])

  const changeCalendarView = useCallback((view: string) => {
    calendarRef.current?.getApi().changeView(view)
  }, [])

  function handleAdjustCredits(action: CreditAction) {
    if (!userId) {
      return
    }

    const parsed = parseCreditAmount(creditAmount)
    if (parsed === null) {
      toast.error('Bitte Credits in 0,1-Schritten größer 0 eingeben.')
      return
    }

    if (creditReason.trim().length < 5) {
      toast.error('Bitte einen Grund mit mindestens 5 Zeichen angeben.')
      return
    }

    let amountPaidCents: number | null = null
    if (action === 'add' && creditPaymentAmount.trim().length > 0) {
      const normalized = creditPaymentAmount.replace(',', '.')
      const parsedAmount = Number(normalized)

      if (!Number.isFinite(parsedAmount) || parsedAmount <= 0) {
        toast.error('Bitte einen gültigen bezahlten Betrag größer 0 angeben.')
        return
      }

      amountPaidCents = Math.round(parsedAmount * 100)
    }

    setCreditAction(action)
    creditMutation.mutate({
      delta: action === 'add' ? parsed : -parsed,
      reason: creditReason.trim(),
      amountPaidCents,
      operationKey: action === 'add' && amountPaidCents ? crypto.randomUUID() : undefined,
    })
  }

  function handleAssignDiscount() {
    if (!selectedDiscountId) {
      toast.error('Bitte einen Rabatt auswählen.')
      return
    }

    discountMutation.mutate({
      discountId: selectedDiscountId,
      action: 'assign',
    })
  }

  function openRemoveDiscountConfirm(assignment: MemberDiscountAssignment) {
    setDiscountToRemove(assignment)
    setRemoveDiscountConfirmOpen(true)
  }

  if (!userId) {
    return <Navigate to="/login" replace />
  }

  if (currentUserQuery.isLoading) {
    return <Skeleton className="h-10 w-72" />
  }

  if (!currentUserQuery.data || !currentUserQuery.data.is_active) {
    return <Navigate to="/login" replace />
  }

  if (!canManageUsers) {
    return <Navigate to="/dashboard" replace />
  }

  if (profileQuery.isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-72" />
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-80 w-full" />
      </div>
    )
  }

  if (profileQuery.isError) {
    return (
      <Alert variant="destructive">
        <AlertDescription>{getErrorMessage(profileQuery.error)}</AlertDescription>
      </Alert>
    )
  }

  const profile = profileQuery.data
  if (!profile) {
    return (
      <Alert variant="destructive">
        <AlertDescription>Benutzer wurde nicht gefunden.</AlertDescription>
      </Alert>
    )
  }

  if (profile.role !== 'member') {
    const fallbackPath =
      currentUserQuery.data?.role === 'trainer' ? '/trainer/customers' : '/admin/customers'
    return <Navigate to={fallbackPath} replace />
  }

  const hideContactDataForTrainer =
    currentUserQuery.data?.role === 'trainer' && profile.role === 'member'
  const currentSubscription = subscriptionQuery.data

  const currentPlanIsRecurring = isRecurringSubscription(currentSubscription)
  const hasCreditBalanceOnly = Boolean(
    currentSubscription && !currentPlanIsRecurring && currentSubscription.remaining_credits > 0,
  )
  const conditionsOutdated = Boolean(
    currentSubscription &&
      subscriptionConditionsQuery.data?.some(
        (c) => c.package_name === currentSubscription.package_name && !c.is_current,
      ),
  )
  const hasLegacyConditions =
    currentSubscription?.price_status === 'legacy' ||
    (currentSubscription?.price_status === 'current' && conditionsOutdated)
  const cancellationPlanned = Boolean(
    currentSubscription && currentPlanIsRecurring && (currentSubscription.stripe_cancel_at_period_end || currentSubscription.stripe_cancel_at),
  )

  return (
    <div className="flex h-full min-h-0 flex-col gap-4 overflow-hidden">
      <Tabs
        value={activeTab}
        onValueChange={handleTabChange}
        className="flex min-h-0 flex-1 flex-col gap-4"
      >
        <div className="flex shrink-0 flex-col gap-3 md:flex-row md:items-end md:justify-between">
          <div className="flex flex-col gap-1">
            <h1 className="text-2xl font-semibold">Benutzerdetails</h1>
            <div className="flex items-center gap-2">
              <p className="text-sm text-muted-foreground">
                Einsicht und Verwaltung für {profile.first_name} {profile.last_name}
              </p>
              <Button
                variant="outline"
                size="sm"
                className="h-7 px-2 text-xs"
                onClick={() => {
                  const prefix = currentUserQuery.data?.role === 'trainer' ? '/trainer' : '/admin';
                  navigate(`${prefix}/notifications?userId=${userId}`);
                }}
              >
                <Megaphone className="mr-1 h-3 w-3" />
                Nachricht senden
              </Button>
            </div>
          </div>
          <div className="-mx-1 overflow-x-auto px-1 py-1 touch-scrollbar-hidden md:mx-0 md:px-0 md:py-0">
            <TabsList className="h-auto min-w-max shrink-0 justify-start gap-1 p-1 md:ml-auto md:w-auto md:justify-end">
              <TabsTrigger value="profile">Allgemein</TabsTrigger>
              <TabsTrigger value="bookings">Termine</TabsTrigger>
              <TabsTrigger value="packages">Pakete</TabsTrigger>
              <TabsTrigger value="credits">Abo/Guthaben</TabsTrigger>
              {isAdmin && (
                <TabsTrigger value="discounts">Rabatte</TabsTrigger>
              )}
              {isAdmin && targetIsMember && (
                <TabsTrigger value="access">Freischaltungen</TabsTrigger>
              )}
              <TabsTrigger value="enrollment">Einbuchung</TabsTrigger>
            </TabsList>
          </div>
        </div>

        <TabsContent value="profile" className="mt-0 min-h-0 flex-1">
          <Card className="flex min-h-0 max-h-full flex-col">
            <CardHeader>
              <CardTitle className="text-lg">Allgemeine Daten</CardTitle>
            </CardHeader>
            <CardContent className="min-h-0 flex-1 overflow-auto space-y-3 text-sm">
              <div className="grid gap-x-6 gap-y-4 sm:grid-cols-2">
                <div className="space-y-1">
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">Name</p>
                  <p className="font-medium">
                    {profile.first_name} {profile.last_name}
                  </p>
                </div>
                {!hideContactDataForTrainer && (
                  <div className="space-y-1">
                    <p className="text-xs uppercase tracking-wide text-muted-foreground">E-Mail</p>
                    <p className="font-medium">{profile.email}</p>
                  </div>
                )}
                <div className="space-y-1">
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">Rolle</p>
                  <Badge variant="secondary">{roleLabel(profile.role)}</Badge>
                </div>
                <div className="space-y-1">
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">Status</p>
                  <Badge variant={profile.is_active ? 'default' : 'destructive'}>
                    {profile.is_active ? 'Aktiv' : 'Deaktiviert'}
                  </Badge>
                </div>
                <div className="space-y-1">
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">Geburtsdatum</p>
                  <p className="font-medium">{formatDate(profile.birth_date ?? null)}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">
                    Gutschein Code
                  </p>
                  <p className="font-medium">{profile.company ?? '-'}</p>
                </div>
                {!hideContactDataForTrainer && (
                  <div className="space-y-1">
                    <p className="text-xs uppercase tracking-wide text-muted-foreground">Telefon</p>
                    <p className="font-medium">{profile.phone_number ?? '-'}</p>
                  </div>
                )}
                {!hideContactDataForTrainer && (
                  <div className="space-y-1 sm:col-span-2">
                    <p className="text-xs uppercase tracking-wide text-muted-foreground">Adresse</p>
                    <p className="font-medium">
                      {profile.street && profile.house_number
                        ? `${profile.street} ${profile.house_number}, ${profile.postal_code ?? ''} ${profile.city ?? ''}`.trim()
                        : '-'}
                    </p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="bookings" className="mt-0 min-h-0 flex-1 overflow-hidden">
          <Card className="flex min-h-0 max-h-full flex-col overflow-hidden">
            <CardHeader>
              <CardTitle className="text-lg">Gebuchte und besuchte Termine</CardTitle>
            </CardHeader>
            <CardContent className="flex min-h-0 flex-1 flex-col overflow-hidden">
              {bookingsQuery.isLoading ? (
                <Skeleton className="h-20 w-full" />
              ) : (
                <div className="flex min-h-0 flex-1 flex-col gap-4">
                  <div className="grid gap-3 md:grid-cols-3">
                    <Select
                      value={courseFilter}
                      onValueChange={(value) => {
                        setBookingsCourseFilter(value)
                        setBookingsPage(1)
                      }}
                    >
                      <SelectTrigger className="bg-card">
                        <SelectValue placeholder="Nach Kurs filtern" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Alle Kurse</SelectItem>
                        {bookingCourseOptions.map((courseName) => (
                          <SelectItem key={courseName} value={courseName}>
                            {courseName}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select
                      value={trainerFilter}
                      onValueChange={(value) => {
                        setBookingsTrainerFilter(value)
                        setBookingsPage(1)
                      }}
                    >
                      <SelectTrigger className="bg-card">
                        <SelectValue placeholder="Nach Trainer filtern" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Alle Trainer</SelectItem>
                        {bookingTrainerOptions.map((trainerName) => (
                          <SelectItem key={trainerName} value={trainerName}>
                            {trainerName}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select
                      value={String(bookingsPageSize)}
                      onValueChange={(value) => {
                        setBookingsPageSize(Number(value))
                        setBookingsPage(1)
                      }}
                    >
                      <SelectTrigger className="bg-card">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {TABLE_PAGE_SIZE_OPTIONS.map((size) => (
                          <SelectItem key={size} value={String(size)}>
                            {size} pro Seite
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {totalFilteredBookings === 0 ? (
                    <p className="text-sm text-muted-foreground">
                      Keine Termine für den aktuellen Filter vorhanden.
                    </p>
                  ) : (
                    <>
                      <Table containerClassName="min-h-0 flex-1 max-h-none">
                        <TableHeader>
                          <TableRow>
                            <TableHead>Termin</TableHead>
                            <TableHead>Kurs</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead>Verbrauch</TableHead>
                            <TableHead>Trainer</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {paginatedBookings.map((booking) => {
                            const bookingState = bookingStatusLabel(booking.status, booking.ends_at)
                            return (
                              <TableRow key={booking.booking_id}>
                                <TableCell>{formatDateTime(booking.starts_at)}</TableCell>
                                <TableCell>
                                  <div className="flex flex-col">
                                    <span>{booking.course_name}</span>
                                    <span className="text-xs text-muted-foreground">
                                      {booking.resource_name ?? '-'}
                                    </span>
                                  </div>
                                </TableCell>
                                <TableCell>
                                  <Badge variant={bookingState.variant}>{bookingState.label}</Badge>
                                </TableCell>
                                <TableCell>
                                  <Badge variant="outline">
                                    {booking.booking_source === 'credit' ? 'Credit' : 'Abo'}
                                  </Badge>
                                </TableCell>
                                <TableCell>{booking.trainer_name ?? '-'}</TableCell>
                              </TableRow>
                            )
                          })}
                        </TableBody>
                      </Table>
                      <div className="flex shrink-0 items-center justify-between gap-3 text-sm text-muted-foreground">
                        <p>
                          {(bookingsCurrentPage - 1) * bookingsPageSize + 1} -{' '}
                          {Math.min(
                            (bookingsCurrentPage - 1) * bookingsPageSize + paginatedBookings.length,
                            totalFilteredBookings,
                          )}{' '}
                          von {totalFilteredBookings}
                        </p>
                        <div className="flex items-center gap-2">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => setBookingsPage(Math.max(1, bookingsCurrentPage - 1))}
                            disabled={bookingsCurrentPage <= 1}
                          >
                            Zurück
                          </Button>
                          <span>
                            Seite {bookingsCurrentPage} / {bookingsTotalPages}
                          </span>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() =>
                              setBookingsPage(Math.min(bookingsTotalPages, bookingsCurrentPage + 1))
                            }
                            disabled={bookingsCurrentPage >= bookingsTotalPages}
                          >
                            Weiter
                          </Button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="packages" className="mt-0 min-h-0 flex-1 overflow-auto">
          {targetIsMember ? (
            <Card className="flex h-full min-h-0 flex-col overflow-hidden">
              <Tabs
                value={packagesContentTab}
                onValueChange={(value) => setPackagesContentTab(value as PackageDetailsTab)}
                className="flex min-h-0 flex-1 flex-col"
              >
                <CardHeader className="flex shrink-0 flex-col gap-3">
                  <CardTitle className="text-lg">{isAdmin ? 'Pakete und Zahlungen' : 'Pakete'}</CardTitle>
                  <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                    <TabsList className="h-auto w-full justify-start gap-1 p-1 md:w-auto">
                      <TabsTrigger value="purchased-packages">Gekaufte Pakete</TabsTrigger>
                      {isAdmin && <TabsTrigger value="payments">Zahlungen</TabsTrigger>}
                    </TabsList>

                    {packagesContentTab === 'purchased-packages' || !isAdmin ? (
                      <Select
                        value={String(packagesPageSize)}
                        onValueChange={(value) => {
                          setPackagesPageSize(Number(value))
                          setPackagesPage(1)
                        }}
                      >
                        <SelectTrigger className="w-full bg-card md:w-[9rem]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {TABLE_PAGE_SIZE_OPTIONS.map((size) => (
                            <SelectItem key={size} value={String(size)}>
                              {size} pro Seite
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Select
                        value={String(billingEntriesPageSize)}
                        onValueChange={(value) => {
                          setBillingEntriesPageSize(Number(value))
                          setBillingEntriesPage(1)
                        }}
                      >
                        <SelectTrigger className="w-full bg-card md:w-[9rem]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {TABLE_PAGE_SIZE_OPTIONS.map((size) => (
                            <SelectItem key={size} value={String(size)}>
                              {size} pro Seite
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                </CardHeader>

                <CardContent className="min-h-0 flex-1 overflow-hidden">
                  <TabsContent
                    value="purchased-packages"
                    className="mt-0 flex h-full min-h-0 flex-col"
                  >
                    {packagesQuery.isLoading ? (
                      <Skeleton className="h-20 w-full" />
                    ) : packages.length === 0 ? (
                      <p className="text-sm text-muted-foreground">Keine Pakete vorhanden.</p>
                    ) : (
                      <div className="flex min-h-0 flex-1 flex-col gap-4">
                        <Table containerClassName="min-h-0 flex-1 max-h-none">
                          <TableHeader>
                            <TableRow>
                              <TableHead>Paket</TableHead>
                              <TableHead>Typ</TableHead>
                              <TableHead>Info</TableHead>
                              <TableHead>Gekauft am</TableHead>
                              <TableHead>Gültig bis</TableHead>
                              <TableHead>Gekaufte Credits</TableHead>
                              <TableHead>Abbuchungen</TableHead>
                              <TableHead>Verbleibende Credits</TableHead>
                              <TableHead>Status</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {paginatedPackages.map((pkg) => {
                              const status = packageStatusLabel(pkg)

                              return (
                                <TableRow key={`${pkg.id}:${pkg.credit_type_id ?? "subscription"}`}>
                                  <TableCell>
                                    {pkg.entry_kind === 'voucher'
                                      ? 'Gutschein'
                                      : pkg.package_type === 'manual_credit'
                                      ? 'Manuelle Buchung'
                                      : pkg.package_name}{pkg.credit_name && <p className="text-xs text-muted-foreground">{pkg.credit_name}</p>}
                                  </TableCell>
                                  <TableCell>
                                    {pkg.entry_kind === 'voucher'
                                      ? 'Gutschein'
                                      : pkg.billing_mode === 'subscription'
                                        ? 'Abo'
                                        : 'Guthaben'}
                                  </TableCell>
                                  <TableCell>
                                    {pkg.entry_kind === 'voucher'
                                      ? (pkg.manual_reason ?? '-')
                                      : pkg.package_type === 'manual_credit'
                                        ? (pkg.manual_reason ?? '-')
                                        : (manualReasonByPackageId.get(pkg.id) ?? '-')}
                                  </TableCell>
                                  <TableCell>{formatDateTime(pkg.created_at)}</TableCell>
                                  <TableCell>{formatDate(pkg.expires_at)}</TableCell>
                                  <TableCell>{pkg.purchased_credits == null ? '-' : formatCreditAmount(pkg.purchased_credits)}</TableCell>
                                  <TableCell>
                                    {pkg.debited_credits > 0 ? formatCreditAmount(pkg.debited_credits) : '-'}
                                  </TableCell>
                                  <TableCell>
                                    {pkg.entry_kind === 'manual_debit' || pkg.billing_mode === 'subscription'
                                      ? '-'
                                      : formatCreditAmount(pkg.remaining_credits)}
                                  </TableCell>
                                  <TableCell>
                                    <Badge variant={status.variant}>{status.label}</Badge>
                                  </TableCell>
                                </TableRow>
                              )
                            })}
                          </TableBody>
                        </Table>
                        <div className="flex items-center justify-between gap-3 text-sm text-muted-foreground">
                          <p>
                            {(packagesCurrentPage - 1) * packagesPageSize + 1} -{' '}
                            {Math.min(
                              (packagesCurrentPage - 1) * packagesPageSize +
                              paginatedPackages.length,
                              totalPackages,
                            )}{' '}
                            von {totalPackages}
                          </p>
                          <div className="flex items-center gap-2">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => setPackagesPage(Math.max(1, packagesCurrentPage - 1))}
                              disabled={packagesCurrentPage <= 1}
                            >
                              Zurück
                            </Button>
                            <span>
                              Seite {packagesCurrentPage} / {packagesTotalPages}
                            </span>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() =>
                                setPackagesPage(
                                  Math.min(packagesTotalPages, packagesCurrentPage + 1),
                                )
                              }
                              disabled={packagesCurrentPage >= packagesTotalPages}
                            >
                              Weiter
                            </Button>
                          </div>
                        </div>
                      </div>
                    )}
                  </TabsContent>

                  {isAdmin && (
                    <TabsContent value="payments" className="mt-0 flex h-full min-h-0 flex-col">
                      {paymentEntriesQuery.isLoading ? (
                        <Skeleton className="h-20 w-full" />
                      ) : paymentEntries.length === 0 ? (
                        <p className="text-sm text-muted-foreground">Keine Zahlungen vorhanden.</p>
                      ) : (
                        <div className="flex min-h-0 flex-1 flex-col gap-4">
                          <PaymentEntriesTable entries={paginatedBillingEntries} />
                          <div className="flex items-center justify-between gap-3 text-sm text-muted-foreground">
                            <p>
                              {(billingEntriesCurrentPage - 1) * billingEntriesPageSize + 1} -{' '}
                              {Math.min(
                                (billingEntriesCurrentPage - 1) * billingEntriesPageSize +
                                paginatedBillingEntries.length,
                                totalBillingEntries,
                              )}{' '}
                              von {totalBillingEntries}
                            </p>
                            <div className="flex items-center gap-2">
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() =>
                                  setBillingEntriesPage(Math.max(1, billingEntriesCurrentPage - 1))
                                }
                                disabled={billingEntriesCurrentPage <= 1}
                              >
                                Zurück
                              </Button>
                              <span>
                                Seite {billingEntriesCurrentPage} / {billingEntriesTotalPages}
                              </span>
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() =>
                                  setBillingEntriesPage(
                                    Math.min(
                                      billingEntriesTotalPages,
                                      billingEntriesCurrentPage + 1,
                                    ),
                                  )
                                }
                                disabled={billingEntriesCurrentPage >= billingEntriesTotalPages}
                              >
                                Weiter
                              </Button>
                            </div>
                          </div>
                        </div>
                      )}
                    </TabsContent>
                  )}
                </CardContent>
              </Tabs>
            </Card>
          ) : (
            <Alert>
              <AlertDescription>
                Paketinformationen sind nur für Teilnehmer verfügbar.
              </AlertDescription>
            </Alert>
          )}
        </TabsContent>

        <TabsContent value="credits" className="mt-0 min-h-0 flex-1 overflow-auto">
          {targetIsMember ? (
            <div className="flex min-h-full min-w-0 flex-col gap-4">
              <div className={`grid items-stretch gap-4 ${isAdmin ? 'lg:grid-cols-2' : ''}`}>
                <Card className="h-full">
                  <CardHeader>
                    <CardTitle className="text-lg">Abo</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {subscriptionQuery.isLoading ? (
                      <Skeleton className="h-16 w-full" />
                    ) : currentSubscription ? (
                      <div className="space-y-3">
                        <div className="flex flex-wrap items-center justify-between gap-3">
                          <div>
                            <p className="font-semibold">{currentSubscription.package_name}</p>
                            {!isAdmin && !currentPlanIsRecurring && (
                              <p className="text-sm text-muted-foreground">
                                Verbleibend: {formatCredits(currentSubscription.remaining_credits)}
                              </p>
                            )}
                          </div>
                          <Badge
                            variant={
                              hasCreditBalanceOnly
                                ? 'secondary'
                                : cancellationPlanned
                                  ? 'secondary'
                                  : currentSubscription.is_active
                                    ? 'default'
                                    : 'destructive'
                            }
                          >
                            {hasCreditBalanceOnly
                              ? 'Guthaben'
                              : cancellationPlanned
                                ? 'Kündigung geplant'
                                : currentSubscription.is_active
                                  ? 'Aktiv'
                                  : currentSubscription.stripe_status === 'past_due'
                                    ? 'Zahlung offen'
                                    : 'Inaktiv'}
                          </Badge>
                        </div>

                        {cancellationPlanned && (currentSubscription.stripe_cancel_at || currentSubscription.current_period_end) && (
                          <p className="text-xs text-amber-600">
                            Kündigung zum{' '}
                            {format(new Date((currentSubscription.stripe_cancel_at || currentSubscription.current_period_end)!), 'dd.MM.yyyy', {
                              locale: de,
                            })}
                            .
                          </p>
                        )}

                        {currentSubscription.expires_at && (
                          <p className="text-xs text-muted-foreground">
                            Gültig bis:{' '}
                            {format(new Date(currentSubscription.expires_at), 'dd.MM.yyyy', {
                              locale: de,
                            })}
                          </p>
                        )}

                        {canManageUsers && currentPlanIsRecurring && (
                          <SubscriptionBenefitList
                            userId={userId!}
                            packageName={currentSubscription.package_name}
                          />
                        )}

                        {currentPlanIsRecurring && (
                          <div className="space-y-2 rounded-md border p-3 text-sm">
                            <div className="flex flex-wrap items-center justify-between gap-2">
                              <span className="font-medium">Abo-Preis</span>
                              <Badge
                                variant={
                                  currentSubscription.price_status === 'failed'
                                    ? 'destructive'
                                    : hasLegacyConditions ||
                                        currentSubscription.price_status === 'pending'
                                      ? 'secondary'
                                      : 'default'
                                }
                              >
                                {currentSubscription.price_status === 'legacy'
                                  ? 'Alter Preis'
                                  : hasLegacyConditions
                                    ? 'Alte Konditionen'
                                    : currentSubscription.price_status === 'pending'
                                    ? 'Preiswechsel ausstehend'
                                    : currentSubscription.price_status === 'failed'
                                      ? 'Preiswechsel fehlgeschlagen'
                                      : 'Aktueller Preis'}
                              </Badge>
                            </div>
                            <div className="grid gap-1 text-xs text-muted-foreground sm:grid-cols-2">
                              <p>
                                Vertragspreis:{' '}
                                {currentSubscription.subscription_price_cents == null
                                  ? 'Unbekannt'
                                  : formatMoney(currentSubscription.subscription_price_cents)}
                              </p>
                              <p>
                                Aktueller Tarif:{' '}
                                {currentSubscription.catalog_price_cents == null
                                  ? 'Unbekannt'
                                  : formatMoney(currentSubscription.catalog_price_cents)}
                              </p>
                              <p className="sm:col-span-2">
                                Stripe-Preis: {currentSubscription.stripe_price_id ?? 'Nicht hinterlegt'}
                              </p>
                            </div>

                            {hasLegacyConditions && (
                              <Alert>
                                <AlertDescription>
                                  {currentSubscription.price_status === 'legacy'
                                    ? 'Dieses Mitglied bezahlt weiterhin einen früheren Abo-Preis.'
                                    : 'Dieses Mitglied hat noch frühere Abo-Konditionen hinterlegt.'}
                                </AlertDescription>
                              </Alert>
                            )}
                            {currentSubscription.price_status === 'failed' && (
                              <p className="text-xs text-destructive">
                                {currentSubscription.price_update_error
                                  ? translateErrorCode(currentSubscription.price_update_error)
                                  : 'Die letzte Stripe-Preisänderung ist fehlgeschlagen.'}
                              </p>
                            )}
                            {cancellationPlanned &&
                              !hasLegacyConditions && (
                                <p className="text-xs text-amber-600">
                                  Kündigung vorgemerkt. Der aktuelle Preis bleibt für eine mögliche
                                  Fortsetzung hinterlegt.
                                </p>
                              )}
                            {currentUserQuery.data?.role === 'admin' &&
                              (hasLegacyConditions ||
                                currentSubscription.price_status === 'failed') && (
                                <Button
                                  type="button"
                                  size="sm"
                                  onClick={() => subscriptionPriceMutation.mutate()}
                                  disabled={subscriptionPriceMutation.isPending}
                                >
                                  {subscriptionPriceMutation.isPending && (
                                    <Loader2Icon className="mr-2 h-4 w-4 animate-spin" />
                                  )}
                                  Ab nächster Abrechnung auf aktuelle Konditionen umstellen
                                </Button>
                              )}
                          </div>
                        )}
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground">Kein aktives Paket vorhanden.</p>
                    )}

                    {canManageUsers && (
                      <div className="space-y-3 border-t pt-4">
                        <h3 className="text-lg font-semibold leading-none tracking-tight">Guthaben</h3>
                        {typedCreditBalancesQuery.isLoading ? (
                          <Skeleton className="h-12 w-full" />
                        ) : (
                          <TypedCreditList
                            balances={typedCreditBalancesQuery.data}
                            showSubscription={currentPlanIsRecurring}
                          />
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {isAdmin && (
                  <Card className="h-full">
                    <CardHeader>
                      <CardTitle className="text-lg">Credits manuell verwalten</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="space-y-2">
                        <Label>Credit-Art</Label>
                        <CreditTypeSelect value={creditTypeId} onChange={setCreditTypeId} />
                        <Label htmlFor="credit-amount">Anzahl Credits</Label>
                        <Input
                          id="credit-amount"
                          type="number"
                          min={0.1}
                          step={0.1}
                          value={creditAmount}
                          onChange={(event) => setCreditAmount(event.target.value)}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="credit-reason">Grund *</Label>
                        <Input
                          id="credit-reason"
                          value={creditReason}
                          onChange={(event) => setCreditReason(event.target.value)}
                          placeholder="z. B. Kulanzgutschrift, Korrektur, Supportfall"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="credit-payment-amount">Bezahlter Betrag (optional)</Label>
                        <Input
                          id="credit-payment-amount"
                          type="number"
                          min={0}
                          step={0.01}
                          value={creditPaymentAmount}
                          onChange={(event) => setCreditPaymentAmount(event.target.value)}
                          placeholder="Nur für Aufbuchungen, z. B. 49,00"
                        />
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <Button
                          type="button"
                          onClick={() => handleAdjustCredits('add')}
                          disabled={creditMutation.isPending}
                        >
                          {creditMutation.isPending && creditAction === 'add'
                            ? 'Wird aufgebucht...'
                            : 'Credits aufbuchen'}
                        </Button>
                        <Button
                          type="button"
                          variant="destructive"
                          onClick={() => handleAdjustCredits('remove')}
                          disabled={creditMutation.isPending}
                        >
                          {creditMutation.isPending && creditAction === 'remove'
                            ? 'Wird abgebucht...'
                            : 'Credits abbuchen'}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>

              <Card className="flex min-h-0 flex-1 flex-col overflow-hidden">
                <CardHeader className="flex flex-row items-center justify-between gap-3">
                  <CardTitle className="text-lg">Manuelle Buchungen</CardTitle>
                  {!creditAdjustmentsQuery.isLoading &&
                    !creditAdjustmentsQuery.isError &&
                    totalCreditAdjustments > 0 && (
                      <Select
                        value={String(creditAdjustmentsPageSize)}
                        onValueChange={(value) => {
                          setCreditAdjustmentsPageSize(Number(value))
                          setCreditAdjustmentsPage(1)
                        }}
                      >
                        <SelectTrigger className="w-[9rem] bg-card">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {TABLE_PAGE_SIZE_OPTIONS.map((size) => (
                            <SelectItem key={size} value={String(size)}>
                              {size} pro Seite
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                </CardHeader>
                <CardContent className="flex min-h-0 flex-1 flex-col gap-4 overflow-hidden">
                  {creditAdjustmentsQuery.isLoading ? (
                    <Skeleton className="h-24 w-full" />
                  ) : creditAdjustmentsQuery.isError ? (
                    <p className="text-sm text-destructive">
                      {getErrorMessage(creditAdjustmentsQuery.error)}
                    </p>
                  ) : totalCreditAdjustments === 0 ? (
                    <p className="text-sm text-muted-foreground">
                      Noch keine manuellen Buchungen vorhanden.
                    </p>
                  ) : (
                    <>
                      <Table containerClassName="min-h-0 flex-1 max-h-none">
                        <TableHeader>
                          <TableRow>
                            <TableHead>Zeitpunkt</TableHead>
                            <TableHead>Bearbeiter</TableHead>
                            <TableHead>Delta</TableHead>
                            <TableHead>Kommentar</TableHead>
                            <TableHead>Vorher</TableHead>
                            <TableHead>Nachher</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {paginatedCreditAdjustments.map((adjustment) => (
                            <TableRow key={adjustment.id}>
                              <TableCell>{formatDateTime(adjustment.created_at)}</TableCell>
                              <TableCell>{adjustment.actor_name}</TableCell>
                              <TableCell>
                                <Badge variant={adjustment.delta > 0 ? 'default' : 'destructive'}>
                                  {adjustment.delta > 0
                                    ? `+${formatCreditAmount(adjustment.delta)}`
                                    : formatCreditAmount(adjustment.delta)} {adjustment.credit_name}
                                </Badge>
                              </TableCell>
                              <TableCell className="max-w-[20rem] truncate">
                                {adjustment.reason}
                              </TableCell>
                              <TableCell>{formatCreditAmount(adjustment.total_before)}</TableCell>
                              <TableCell>{formatCreditAmount(adjustment.total_after)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>

                      <div className="flex shrink-0 flex-wrap items-center justify-between gap-3 text-sm text-muted-foreground">
                        <p>
                          {(creditAdjustmentsCurrentPage - 1) * creditAdjustmentsPageSize + 1} -{' '}
                          {Math.min(
                            (creditAdjustmentsCurrentPage - 1) * creditAdjustmentsPageSize +
                            paginatedCreditAdjustments.length,
                            totalCreditAdjustments,
                          )}{' '}
                          von {totalCreditAdjustments}
                        </p>
                        <div className="flex items-center gap-2">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() =>
                              setCreditAdjustmentsPage(Math.max(1, creditAdjustmentsCurrentPage - 1))
                            }
                            disabled={creditAdjustmentsCurrentPage <= 1}
                          >
                            Zurück
                          </Button>
                          <span>
                            Seite {creditAdjustmentsCurrentPage} / {creditAdjustmentsTotalPages}
                          </span>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() =>
                              setCreditAdjustmentsPage(
                                Math.min(
                                  creditAdjustmentsTotalPages,
                                  creditAdjustmentsCurrentPage + 1,
                                ),
                              )
                            }
                            disabled={creditAdjustmentsCurrentPage >= creditAdjustmentsTotalPages}
                          >
                            Weiter
                          </Button>
                        </div>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            </div>
          ) : (
            <Alert>
              <AlertDescription>
                Abo- und Guthabenverwaltung ist nur für Teilnehmer verfügbar.
              </AlertDescription>
            </Alert>
          )}
        </TabsContent>

        <TabsContent value="discounts" className="mt-0 min-h-0 flex-1 overflow-auto">
          {currentUserQuery.data?.role === 'admin' && targetIsMember ? (
            <Card className="flex min-h-0 max-h-full flex-col">
              <CardHeader>
                <CardTitle className="text-lg">Rabatte</CardTitle>
              </CardHeader>
              <CardContent className="min-h-0 flex-1 space-y-4 overflow-auto">
                <div className="grid gap-3 md:grid-cols-[1fr_auto]">
                  <Select
                    value={selectedDiscountId}
                    onValueChange={setSelectedDiscountId}
                    disabled={discountsQuery.isLoading || availableDiscounts.length === 0}
                  >
                    <SelectTrigger className="bg-card">
                      <SelectValue
                        placeholder={
                          availableDiscounts.length === 0
                            ? 'Keine aktiven Rabatte verfügbar'
                            : 'Rabatt auswählen'
                        }
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {availableDiscounts.map((discount) => (
                        <SelectItem key={discount.id} value={discount.id}>
                          {discount.title} · {formatDiscountValue(discount)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    type="button"
                    onClick={handleAssignDiscount}
                    disabled={
                      !selectedDiscountId ||
                      discountMutation.isPending ||
                      availableDiscounts.length === 0
                    }
                  >
                    <PlusIcon className="mr-2 h-4 w-4" />
                    Zuweisen
                  </Button>
                </div>

                {discountAssignmentsQuery.isLoading || discountsQuery.isLoading ? (
                  <Skeleton className="h-24 w-full" />
                ) : assignedDiscounts.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    Diesem Teilnehmer sind keine Rabatte zugewiesen.
                  </p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Rabatt</TableHead>
                        <TableHead>Wert</TableHead>
                        <TableHead>Zugewiesen am</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Aktion</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {assignedDiscounts.map((assignment) => (
                        <TableRow key={assignment.assignment_id}>
                          <TableCell className="font-medium">{assignment.title}</TableCell>
                          <TableCell>{formatDiscountValue(assignment)}</TableCell>
                          <TableCell>{formatDateTime(assignment.assigned_at)}</TableCell>
                          <TableCell>
                            <Badge variant={assignment.is_active ? 'default' : 'secondary'}>
                              {assignment.is_active ? 'Aktiv' : 'Rabatt inaktiv'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex justify-end">
                              <Button
                                type="button"
                                variant="destructive"
                                size="sm"
                                disabled={discountMutation.isPending}
                                onClick={() => openRemoveDiscountConfirm(assignment)}
                              >
                                <Trash2Icon className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          ) : (
            <Alert>
              <AlertDescription>Rabatte können nur von Admins verwaltet werden.</AlertDescription>
            </Alert>
          )}
        </TabsContent>

        <Dialog
          open={removeDiscountConfirmOpen}
          onOpenChange={(open) => {
            setRemoveDiscountConfirmOpen(open)
            if (!open && !discountMutation.isPending) {
              setDiscountToRemove(null)
            }
          }}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Rabatt entfernen?</DialogTitle>
              <DialogDescription>
                Der Rabatt wird vom Teilnehmer entfernt und bei einem aktiven Abo nicht mehr auf
                künftige Stripe-Rechnungen angewendet.
              </DialogDescription>
            </DialogHeader>

            {discountToRemove ? (
              <div className="rounded-md border p-3 text-sm">
                <span className="text-muted-foreground">Rabatt: </span>
                <span className="font-medium">{discountToRemove.title}</span>
                <span className="text-muted-foreground"> · </span>
                <span>{formatDiscountValue(discountToRemove)}</span>
              </div>
            ) : null}

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setRemoveDiscountConfirmOpen(false)
                  setDiscountToRemove(null)
                }}
                disabled={discountMutation.isPending}
              >
                Abbrechen
              </Button>
              <Button
                type="button"
                variant="destructive"
                onClick={() => {
                  if (!discountToRemove) {
                    return
                  }

                  discountMutation.mutate({
                    discountId: discountToRemove.discount_id,
                    action: 'remove',
                  })
                }}
                disabled={discountMutation.isPending || !discountToRemove}
              >
                {discountMutation.isPending ? (
                  <>
                    <Loader2Icon className="mr-2 h-4 w-4 animate-spin" />
                    Entfernt ...
                  </>
                ) : (
                  'Ja, entfernen'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {isAdmin && targetIsMember && (
          <TabsContent value="access" className="mt-0 min-h-0 flex-1 overflow-auto">
            <Card className="flex min-h-0 max-h-full flex-col">
              <CardHeader>
                <CardTitle className="text-lg">Kursfreischaltungen</CardTitle>
              </CardHeader>
              <CardContent className="min-h-0 flex-1 overflow-auto">
                <CustomerCourseAccess userId={userId!} />
              </CardContent>
            </Card>
          </TabsContent>
        )}

        <TabsContent value="enrollment" className="mt-0 flex min-h-0 flex-1 overflow-hidden">
          {targetIsMember ? (
            <Card className="flex h-full min-h-0 flex-1 flex-col overflow-hidden">
              <CardHeader>
                <CardTitle className="text-lg">Teilnehmer über Kalender einbuchen</CardTitle>
              </CardHeader>
              <CardContent className="min-h-0 flex-1 overflow-hidden">
                {showMobileToolbar && (
                  <MobileCalendarToolbar
                    currentView={effectiveCalendarView}
                    title={calendarTitle}
                    views={calendarViews}
                    onPrev={() => navigateCalendar('prev')}
                    onNext={() => navigateCalendar('next')}
                    onToday={goToToday}
                    onViewChange={changeCalendarView}
                  />
                )}
                <div className="h-full min-h-0 motionlab-member-calendar motionlab-member-calendar-contained">
                  <FullCalendar
                    ref={calendarRef}
                    key={calendarKey}
                    plugins={[timeGridPlugin, dayGridPlugin, interactionPlugin]}
                    locale={deLocale}
                    initialView={currentCalendarView === 'dayGridMonth' ? 'dayGridMonth' : calendarView}
                    duration={calendarDuration}
                    scrollTime={DEFAULT_SCROLL_TIME}
                    scrollTimeReset={false}
                    headerToolbar={showMobileToolbar
                      ? false
                      : {
                        left: 'prev,next today',
                        center: 'title',
                        right: 'timeGridDay,timeGridWeek,dayGridMonth',
                      }}
                    buttonText={{
                      today: 'Heute',
                      timeGridDay: 'Tag',
                      timeGridWeek: 'Woche',
                      dayGridMonth: 'Monat',
                    }}
                    allDaySlot={false}
                    slotMinTime={START_TIME}
                    slotMaxTime={END_TIME}
                    slotDuration={SLOT_DURATION}
                    height="100%"
                    expandRows={true}
                    events={calendarEvents}
                    datesSet={handleDatesSet}
                    eventClick={handleEventClick}
                    eventContent={renderEventContent}
                    eventTimeFormat={{
                      hour: '2-digit',
                      minute: '2-digit',
                      hour12: false,
                    }}
                  />
                </div>
              </CardContent>
            </Card>
          ) : (
            <Alert>
              <AlertDescription>
                Einbuchung über den Kalender ist nur für Teilnehmer verfügbar.
              </AlertDescription>
            </Alert>
          )}
        </TabsContent>
      </Tabs>

      <CancelBookingConfirmDialog
        open={confirmCancelOpen && selectedSessionBooking != null}
        onOpenChange={setConfirmCancelOpen}
        label={selectedSession && `${selectedSession.courseName} · ${format(new Date(selectedSession.startsAt), "dd.MM.yyyy 'um' HH:mm", { locale: de })}`}
        memberName={profile?.full_name ?? profile?.email}
        onConfirm={() => {
          setConfirmCancelOpen(false)
          if (selectedSessionBooking) cancelBookingMutation.mutate(selectedSessionBooking.booking_id)
        }}
      />

      <SessionDetailDialog
        open={selectedSession != null}
        onOpenChange={(open) => {
          if (!open) {
            setSelectedSessionId(null)
          }
        }}
        session={selectedSessionDetail}
        title={isSelectedSessionBooked ? 'Buchung stornieren' : 'Teilnehmer einbuchen'}
        description={
          isSelectedSessionBooked
            ? 'Bestätige die Stornierung für diesen Termin.'
            : 'Bestätige die Einbuchung für den ausgewählten Termin.'
        }
        enforceCancellationDeadline={false}
        footer={
          <div className="flex w-full flex-col gap-2">
            {!isSelectedSessionBooked && selectedSession && (
              bookingQuoteQuery.data?.can_book === false &&
              bookingQuoteQuery.data.block_reason &&
              bookingQuoteQuery.data.block_reason !== 'INSUFFICIENT_CREDITS' ? (
                <Alert variant="destructive" className="bg-destructive/5">
                  <AlertCircleIcon />
                  <AlertTitle>
                    {STAFF_BOOKING_BLOCKS[bookingQuoteQuery.data.block_reason]?.title ??
                      'Einbuchung nicht möglich'}
                  </AlertTitle>
                  <AlertDescription>
                    {STAFF_BOOKING_BLOCKS[bookingQuoteQuery.data.block_reason]?.description ??
                      getErrorMessage(new Error(bookingQuoteQuery.data.block_reason))}
                  </AlertDescription>
                </Alert>
              ) : bookingQuoteQuery.data?.can_book === false ? (
                <Alert variant="destructive" className="bg-destructive/5">
                  <AlertCircleIcon />
                  <AlertTitle>Nicht genügend Credits</AlertTitle>
                  <AlertDescription>
                    <p>
                      Benötigt werden{' '}
                      <strong>{formatCourseCredits(bookingQuoteQuery.data.credits_required)}</strong>.
                    </p>
                    <div
                      className={cn(
                        'mt-3 grid gap-2',
                        currentPlanIsRecurring ? 'grid-cols-2' : 'grid-cols-1',
                      )}
                    >
                      {currentPlanIsRecurring && (
                        <div className="rounded-md border border-destructive/20 bg-background/80 px-2.5 py-2 text-foreground">
                          <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                            Abo verfügbar
                          </p>
                          <p className="mt-0.5 text-sm font-semibold">
                            {formatCredits(bookingQuoteQuery.data.subscription_credits_used)}
                          </p>
                        </div>
                      )}
                      <div className="rounded-md border border-destructive/20 bg-background/80 px-2.5 py-2 text-foreground">
                        <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                          Guthaben verfügbar
                        </p>
                        <p className="mt-0.5 text-sm font-semibold">
                          {formatCredits(bookingQuoteQuery.data.available_balance_credits)}
                        </p>
                      </div>
                    </div>
                  </AlertDescription>
                </Alert>
              ) : (
                <div className="rounded-lg border border-primary/25 bg-primary/5 p-3">
                  <div className="flex items-start gap-2.5">
                    <div className="mt-0.5 rounded-full bg-primary/10 p-1.5 text-primary">
                      <CoinsIcon className="h-4 w-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-sm font-semibold text-foreground">Credit-Abrechnung</p>
                        <span className="shrink-0 rounded-full border border-primary/20 bg-background px-2 py-0.5 text-xs font-semibold text-foreground">
                          {formatCourseCredits(
                            bookingQuoteQuery.data?.credits_required ??
                              selectedSession.creditsPerSession,
                          )}
                        </span>
                      </div>
                      {(bookingQuoteQuery.data?.credits_required ?? selectedSession.creditsPerSession) === 0 ? (
                        <p className="mt-1.5 text-xs text-muted-foreground">
                          Kostenlos – es werden keine Credits abgezogen.
                        </p>
                      ) : bookingQuoteQuery.isLoading || subscriptionQuery.isLoading ? (
                        <p className="mt-1.5 text-xs text-muted-foreground">
                          Die Aufteilung wird berechnet ...
                        </p>
                      ) : bookingQuoteQuery.data ? (
                        <div
                          className={cn(
                            'mt-2 grid gap-2',
                            bookingQuoteQuery.data.balance_credits_used > 0 &&
                              (bookingQuoteQuery.data.subscription_credits_used > 0 ||
                                currentPlanIsRecurring)
                              ? 'grid-cols-2'
                              : 'grid-cols-1',
                          )}
                        >
                          {(bookingQuoteQuery.data.subscription_credits_used > 0 ||
                            currentPlanIsRecurring) && (
                            <div className="rounded-md border bg-background/80 px-2.5 py-2">
                              <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                                Abo
                              </p>
                              <p className="mt-0.5 text-sm font-semibold text-foreground">
                                {formatCredits(
                                  bookingQuoteQuery.data.subscription_credits_used,
                                )}
                              </p>
                            </div>
                          )}
                          {bookingQuoteQuery.data.balance_credits_used > 0 && (
                            <div className="rounded-md border bg-background/80 px-2.5 py-2">
                              <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                                Guthaben
                              </p>
                              <p className="mt-0.5 text-sm font-semibold text-foreground">
                                {formatCredits(bookingQuoteQuery.data.balance_credits_used)}
                              </p>
                            </div>
                          )}
                        </div>
                      ) : (
                        <p className="mt-1.5 text-xs text-muted-foreground">
                          Die genaue Aufteilung wird beim Buchen geprüft.
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )
            )}
            <div className="flex w-full justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setSelectedSessionId(null)}
                disabled={bookingMutation.isPending || cancelBookingMutation.isPending}
              >
                Abbrechen
              </Button>
              {isSelectedSessionBooked ? (
                <Button
                  variant="destructive"
                  onClick={() => setConfirmCancelOpen(true)}
                  disabled={cancelBookingMutation.isPending || !selectedSessionBooking}
                >
                  {cancelBookingMutation.isPending ? 'Wird storniert...' : 'Stornieren'}
                </Button>
              ) : (
                <Button
                  onClick={() => {
                    if (!selectedSession) {
                      return
                    }
                    bookingMutation.mutate(selectedSession.id)
                  }}
                  disabled={
                    bookingMutation.isPending ||
                    bookingQuoteQuery.isLoading ||
                    subscriptionQuery.isLoading ||
                    bookingQuoteQuery.data?.can_book === false ||
                    !selectedSession
                  }
                >
                  {bookingMutation.isPending ? 'Wird eingebucht...' : 'Einbuchen'}
                </Button>
              )}
            </div>
          </div>
        }
      />
    </div>
  )
}
